import {Button, Drawer, DrawerProps, message, Space, Spin} from 'antd';
import React, {useRef} from 'react';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import {ColumnList, deleteColumn, getColumnPage} from './service';
import useTable from 'Utils/Hooks/useTable';
import Style from './index.module.scss';
import {PlusOutlined} from '@ant-design/icons';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import EditModal from './Components/EditModal';
import {errorMessage} from 'Utils/common';
import DeleteConfirm from 'Components/DeleteConfirm';
import {useDict} from '../../store';
import {ColumnContext} from './context';
import OperateModal from './Components/OperateModal';
import LocateModal from './Components/LocateModal';

interface Props extends DrawerProps {
    onOk: () => void;
    siteId: string;
}

const Index = ({onOk, siteId, ...props}: Props) => {
    const dict = useDict();
    const {tableInit} = useTable(getColumnPage, {data: {siteId}});
    const tableRef = useRef<TableContext>(null);
    const editModal = useModalHandler({
        component: EditModal,
    });
    const operateModal = useModalHandler({
        component: OperateModal,
        type: 'drawer',
    });
    const locateModal = useModalHandler({
        component: LocateModal,
        type: 'drawer',
    });

    const [deleteAction, {loading: deleteLoading}] = useRequestFn(deleteColumn);

    function reload() {
        tableRef?.current?.refresh();
    }

    async function onDelete(id: string) {
        try {
            await deleteAction(id);
            await reload();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const columns: ProColumnsType<ColumnList> = [
        {
            title: '栏目名称',
            dataIndex: 'catname',
        },
        {
            title: '网址',
            dataIndex: 'caturl',
            render: value => (
                <a href={value} target="_blank">
                    {value}
                </a>
            ),
        },
        {
            title: '栏目类型',
            dataIndex: 'cattype',
            width: 160,
            render: dict.ColumnType.getName,
        },
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            width: 160,
            align: 'center',
            render: (id: string, data) => (
                <Space>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            operateModal.open({
                                catid: id,
                                onOk: () => {
                                    operateModal.close();
                                    reload();
                                },
                            });
                        }}>
                        操作设置
                    </Button>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            locateModal.open({
                                catid: id,
                                onOk: () => {
                                    locateModal.close();
                                    reload();
                                },
                            });
                        }}>
                        定位设置
                    </Button>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            editModal.open({
                                data,
                                onOk: () => {
                                    editModal.close();
                                    reload();
                                },
                            });
                        }}>
                        修改
                    </Button>
                    <DeleteConfirm onSuccess={() => onDelete(id)}>
                        <Button size="small" type="link">
                            删除
                        </Button>
                    </DeleteConfirm>
                </Space>
            ),
        },
    ];

    const schema = {
        type: 'object',
        labelWidth: 100,
        properties: {
            catname: {
                title: '栏目名称',
                type: 'string',
            },
        },
    };

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                <Button
                    type="primary"
                    onClick={() =>
                        editModal.open({
                            onOk: () => {
                                editModal.close();
                                reload();
                            },
                        })
                    }
                    icon={<PlusOutlined />}>
                    新增
                </Button>
            </Space>
        </div>
    );

    return (
        <ColumnContext.Provider value={{siteId}}>
            <Drawer
                title="栏目管理"
                className={Style.root}
                {...props}
                onClose={onOk}
                width="90%">
                <Spin spinning={deleteLoading}>
                    <TableRender
                        rowKey="id"
                        ref={tableRef}
                        columns={columns}
                        search={{schema, column: 4}}
                        toolbarRender={toolBar}
                        scroll={{x: 'max-content'}}
                        {...tableInit()}
                    />
                </Spin>
                {editModal.render()}
                {operateModal.render()}
                {locateModal.render()}
            </Drawer>
        </ColumnContext.Provider>
    );
};

export default Index;
